import type { Dispatch, SetStateAction } from "react"

import type { ToggleableColumn } from "../components/test-case-list/TestCaseColumnVisibility"
import { EVENTS, useEventBus } from "../event-bus"
import type {
  EditableTestCase,
  HistoryEntry,
  PendingChanges,
  TestSetMetadata,
} from "../model"
import {
  getAcceptedPendingAdditionState,
  getAcceptedPendingDeletionState,
  getAcceptedPendingEditState,
  getColumnsToShowForTestCases,
  getCopiedTestCase,
  getDeletedTestCaseState,
  getHydratedEditorState,
  getRejectedPendingAdditionState,
  getRejectedPendingDeletionState,
  getRejectedPendingEditState,
  getResetEditorState,
  getRevertedHistoryState,
  getSavedEditorState,
  getSelectAllState,
  getToggledSelectionState,
  getUndoState,
  getUpdatedMetadata,
} from "../utils/testSetEditorContextState"

interface UseTestSetEditorEventHandlersParams {
  activeAgentId: string
  clearLocalMediaAttachments: () => void
  getLastToggledIndex: () => number | null
  history: HistoryEntry[]
  initialActiveAgentId: string
  initialMetadata: TestSetMetadata
  initialSelectedAgentIds: string[]
  initialTestCases: EditableTestCase[]
  isInitialized: boolean
  metadata: TestSetMetadata
  pendingChanges: PendingChanges | null
  saveToHistory: (
    actionType: HistoryEntry["actionType"],
    description: string,
  ) => void
  selectedAgentIds: string[]
  selectedTestCaseId: string | null
  selectedTestCaseIds: Set<string>
  setLastToggledIndex: (index: number | null) => void
  setActiveAgentId: Dispatch<SetStateAction<string>>
  setHistory: Dispatch<SetStateAction<HistoryEntry[]>>
  setInitialActiveAgentId: Dispatch<SetStateAction<string>>
  setInitialMetadata: Dispatch<SetStateAction<TestSetMetadata>>
  setInitialSelectedAgentIds: Dispatch<SetStateAction<string[]>>
  setInitialTestCases: Dispatch<SetStateAction<EditableTestCase[]>>
  setIsInitialized: Dispatch<SetStateAction<boolean>>
  setMetadata: Dispatch<SetStateAction<TestSetMetadata>>
  setPendingChanges: Dispatch<SetStateAction<PendingChanges | null>>
  setSelectedAgentIds: Dispatch<SetStateAction<string[]>>
  setSelectedTestCaseId: Dispatch<SetStateAction<string | null>>
  setSelectedTestCaseIds: Dispatch<SetStateAction<Set<string>>>
  setTestCases: Dispatch<SetStateAction<EditableTestCase[]>>
  setVisibleColumns: Dispatch<SetStateAction<Set<ToggleableColumn>>>
  testCases: EditableTestCase[]
  /**
   * Every row the table renders, in row order: the test cases left by search/type filtering, then the staged additions below them. Index-based selection (shift-select range, select-all) operates on this list, so it respects the filtered view and reaches a staged row. Defaults to `testCases` when omitted.
   */
  selectableTestCases?: EditableTestCase[]
}

export function useTestSetEditorEventHandlers({
  activeAgentId,
  clearLocalMediaAttachments,
  getLastToggledIndex,
  history,
  initialActiveAgentId,
  initialMetadata,
  initialSelectedAgentIds,
  initialTestCases,
  isInitialized,
  metadata,
  pendingChanges,
  saveToHistory,
  selectableTestCases,
  selectedAgentIds,
  selectedTestCaseId,
  selectedTestCaseIds,
  setLastToggledIndex,
  setActiveAgentId,
  setHistory,
  setInitialActiveAgentId,
  setInitialMetadata,
  setInitialSelectedAgentIds,
  setInitialTestCases,
  setIsInitialized,
  setMetadata,
  setPendingChanges,
  setSelectedAgentIds,
  setSelectedTestCaseId,
  setSelectedTestCaseIds,
  setTestCases,
  setVisibleColumns,
  testCases,
}: UseTestSetEditorEventHandlersParams) {
  // Selection by row index targets the rendered list so ranges and select-all never reach rows hidden by the active filters.
  const selectionTestCases = selectableTestCases ?? testCases
  useEventBus(EVENTS.TEST_CASE_SELECT, ({ testCaseId }) => {
    setSelectedTestCaseId(testCaseId)
  })

  useEventBus(EVENTS.TEST_CASE_UPDATE, ({ testCaseId, updates }) => {
    setTestCases((prev) =>
      prev.map((testCase) =>
        testCase.id === testCaseId ? { ...testCase, ...updates } : testCase,
      ),
    )
  })

  useEventBus(EVENTS.TEST_CASE_DELETE, ({ testCaseId }) => {
    const nextState = getDeletedTestCaseState({
      pendingChanges,
      selectedTestCaseId,
      selectedTestCaseIds,
      testCaseIds: [testCaseId],
      testCases,
    })

    setTestCases(nextState.testCases)
    setSelectedTestCaseId(nextState.selectedTestCaseId)
    setSelectedTestCaseIds(nextState.selectedTestCaseIds)
    setPendingChanges(nextState.pendingChanges)
  })

  useEventBus(EVENTS.TEST_CASE_ADD, ({ testCase }) => {
    setTestCases((prev) => [...prev, testCase])
    setSelectedTestCaseId(testCase.id)
  })

  useEventBus(EVENTS.PERSISTED_TEST_CASE_ADD, ({ testCase }) => {
    const appendIfMissing = (cases: EditableTestCase[]) =>
      cases.some(({ id }) => id === testCase.id) ? cases : [...cases, testCase]
    setTestCases(appendIfMissing)
    setInitialTestCases(appendIfMissing)
    setVisibleColumns(
      (columns) =>
        new Set([...columns, ...getColumnsToShowForTestCases([testCase])]),
    )
    setHistory((entries) =>
      entries.map((entry) => ({
        ...entry,
        testCases: appendIfMissing(entry.testCases),
      })),
    )
  })

  useEventBus(EVENTS.TEST_CASE_COPY, ({ testCaseId }) => {
    const copiedTestCase = getCopiedTestCase(
      testCases,
      testCaseId,
      `new-${Date.now()}`,
    )

    if (copiedTestCase) {
      setTestCases((prev) => [...prev, copiedTestCase])
      setSelectedTestCaseId(copiedTestCase.id)
    }
  })

  useEventBus(EVENTS.METADATA_UPDATE, (updates) => {
    setMetadata((prev) => getUpdatedMetadata(prev, updates))
  })

  useEventBus(EVENTS.HYDRATE_FROM_SERVER, (payload) => {
    const hydratedState = getHydratedEditorState(payload)

    setMetadata(hydratedState.metadata)
    setInitialMetadata(hydratedState.initialMetadata)
    setTestCases(hydratedState.testCases)
    setInitialTestCases(hydratedState.initialTestCases)
    setSelectedTestCaseId(hydratedState.selectedTestCaseId)
    setSelectedAgentIds(hydratedState.selectedAgentIds)
    setInitialSelectedAgentIds(hydratedState.initialSelectedAgentIds)
    setActiveAgentId(hydratedState.activeAgentId)
    setInitialActiveAgentId(hydratedState.initialActiveAgentId)
    setPendingChanges(hydratedState.pendingChanges)
    setHistory(hydratedState.history)
    clearLocalMediaAttachments()

    const columnsToShow = getColumnsToShowForTestCases(hydratedState.testCases)
    if (columnsToShow.length > 0) {
      setVisibleColumns((prev) => {
        const next = new Set(prev)
        for (const col of columnsToShow) {
          next.add(col)
        }
        return next.size === prev.size ? prev : next
      })
    }

    if (!isInitialized) {
      setIsInitialized(true)
    }
  })

  useEventBus(EVENTS.COPILOT_UPDATE, ({ updates }) => {
    const updateCount = updates.length
    saveToHistory(
      "COPILOT_UPDATE",
      `Updated ${updateCount} test case${updateCount !== 1 ? "s" : ""}`,
    )

    setTestCases((prev) =>
      prev.map((testCase) => {
        const update = updates.find((candidate) => candidate.id === testCase.id)
        return update ? { ...testCase, ...update.updates } : testCase
      }),
    )
  })

  useEventBus(EVENTS.COPILOT_ADD, ({ testCases: newTestCases }) => {
    const addCount = newTestCases.length
    saveToHistory(
      "COPILOT_ADD",
      `Added ${addCount} test case${addCount !== 1 ? "s" : ""}`,
    )

    setTestCases((prev) => [...prev, ...newTestCases])

    const columnsToShow = getColumnsToShowForTestCases(newTestCases)
    if (columnsToShow.length > 0) {
      setVisibleColumns((prev) => {
        const next = new Set(prev)
        for (const col of columnsToShow) {
          next.add(col)
        }
        return next.size === prev.size ? prev : next
      })
    }
  })

  useEventBus(EVENTS.COPILOT_DELETE, ({ testCaseIds }) => {
    const deleteCount = testCaseIds.length
    saveToHistory(
      "COPILOT_DELETE",
      `Deleted ${deleteCount} test case${deleteCount !== 1 ? "s" : ""}`,
    )

    const nextState = getDeletedTestCaseState({
      pendingChanges,
      selectedTestCaseId,
      selectedTestCaseIds,
      testCaseIds,
      testCases,
    })

    setTestCases(nextState.testCases)
    setSelectedTestCaseId(nextState.selectedTestCaseId)
    setSelectedTestCaseIds(nextState.selectedTestCaseIds)
    setPendingChanges(nextState.pendingChanges)
  })

  useEventBus(EVENTS.BULK_ADD, ({ testCases: newTestCases }) => {
    const addCount = newTestCases.length
    saveToHistory(
      "BULK_ADD",
      `Duplicated ${addCount} test case${addCount !== 1 ? "s" : ""}`,
    )

    setTestCases((prev) => [...prev, ...newTestCases])
    if (newTestCases.length > 0) {
      setSelectedTestCaseId(newTestCases[0].id)
    }
  })

  useEventBus(EVENTS.BULK_DELETE, ({ testCaseIds }) => {
    const deleteCount = testCaseIds.length
    saveToHistory(
      "BULK_DELETE",
      `Deleted ${deleteCount} test case${deleteCount !== 1 ? "s" : ""}`,
    )

    const nextState = getDeletedTestCaseState({
      pendingChanges,
      selectedTestCaseId,
      selectedTestCaseIds,
      testCaseIds,
      testCases,
    })

    setTestCases(nextState.testCases)
    setSelectedTestCaseId(nextState.selectedTestCaseId)
    setSelectedTestCaseIds(nextState.selectedTestCaseIds)
    setPendingChanges(nextState.pendingChanges)
  })

  useEventBus(EVENTS.PENDING_CHANGES_UPDATE, ({ changes }) => {
    setPendingChanges(changes)

    if (changes?.type === "ADD") {
      const columnsToShow = getColumnsToShowForTestCases(changes.data)
      if (columnsToShow.length > 0) {
        setVisibleColumns((prev) => {
          const next = new Set(prev)
          for (const col of columnsToShow) {
            next.add(col)
          }
          return next.size === prev.size ? prev : next
        })
      }
    }
  })

  useEventBus(EVENTS.SAVE_COMPLETE, () => {
    const savedState = getSavedEditorState({
      activeAgentId,
      metadata,
      selectedAgentIds,
      testCases,
    })

    setInitialTestCases(savedState.initialTestCases)
    setInitialMetadata(savedState.initialMetadata)
    setInitialSelectedAgentIds(savedState.initialSelectedAgentIds)
    setInitialActiveAgentId(savedState.initialActiveAgentId)
    setPendingChanges(savedState.pendingChanges)
    setHistory(savedState.history)
    clearLocalMediaAttachments()
  })

  useEventBus(EVENTS.CANCEL_REQUEST, () => {
    const resetState = getResetEditorState({
      initialActiveAgentId,
      initialMetadata,
      initialSelectedAgentIds,
      initialTestCases,
    })

    setTestCases(resetState.testCases)
    setMetadata(resetState.metadata)
    setSelectedTestCaseId(resetState.selectedTestCaseId)
    setSelectedAgentIds(resetState.selectedAgentIds)
    setActiveAgentId(resetState.activeAgentId)
    setPendingChanges(resetState.pendingChanges)
    setHistory(resetState.history)
    clearLocalMediaAttachments()
  })

  useEventBus(EVENTS.UNDO, () => {
    const undoState = getUndoState(history)
    if (!undoState) {
      return
    }

    setTestCases(undoState.testCases)
    setSelectedTestCaseId(undoState.selectedTestCaseId)
    setHistory(undoState.history)
  })

  useEventBus(EVENTS.REVERT_TO_HISTORY, ({ historyIndex }) => {
    const revertedHistoryState = getRevertedHistoryState(history, historyIndex)
    if (!revertedHistoryState) {
      return
    }

    setTestCases(revertedHistoryState.testCases)
    setSelectedTestCaseId(revertedHistoryState.selectedTestCaseId)
    setHistory(revertedHistoryState.history)
  })

  useEventBus(
    EVENTS.TEST_CASE_TOGGLE_SELECT,
    ({ testCaseId, index, shiftKey }) => {
      const nextSelectionState = getToggledSelectionState({
        index,
        lastToggledIndex: getLastToggledIndex(),
        selectedTestCaseIds,
        shiftKey,
        testCaseId,
        testCases: selectionTestCases,
      })

      setSelectedTestCaseIds(nextSelectionState.selectedTestCaseIds)
      setLastToggledIndex(nextSelectionState.lastToggledIndex)
    },
  )

  useEventBus(EVENTS.TEST_CASE_SELECT_ALL, () => {
    const selectAllState = getSelectAllState(selectionTestCases)
    setSelectedTestCaseIds(selectAllState.selectedTestCaseIds)
    setLastToggledIndex(selectAllState.lastToggledIndex)
  })

  useEventBus(EVENTS.TEST_CASE_CLEAR_SELECTION, () => {
    setSelectedTestCaseIds(new Set())
    setLastToggledIndex(null)
  })

  useEventBus(EVENTS.ACCEPT_PENDING_EDIT, ({ testCaseId }) => {
    const acceptedState = getAcceptedPendingEditState(
      pendingChanges,
      testCases,
      testCaseId,
    )
    if (!acceptedState) {
      return
    }

    setTestCases(acceptedState.testCases)
    setPendingChanges(acceptedState.pendingChanges)
  })

  useEventBus(EVENTS.DENY_PENDING_EDIT, ({ testCaseId }) => {
    const nextPendingChanges = getRejectedPendingEditState(
      pendingChanges,
      testCaseId,
    )
    if (nextPendingChanges === null && pendingChanges?.type !== "EDIT") {
      return
    }

    setPendingChanges(nextPendingChanges)
  })

  useEventBus(EVENTS.ACCEPT_PENDING_ADDITION, ({ index }) => {
    const acceptedState = getAcceptedPendingAdditionState(
      pendingChanges,
      testCases,
      index,
    )
    if (!acceptedState) {
      return
    }

    setTestCases(acceptedState.testCases)
    setPendingChanges(acceptedState.pendingChanges)
  })

  useEventBus(EVENTS.DENY_PENDING_ADDITION, ({ index }) => {
    const nextPendingChanges = getRejectedPendingAdditionState(
      pendingChanges,
      index,
    )
    if (nextPendingChanges === null && pendingChanges?.type !== "ADD") {
      return
    }

    setPendingChanges(nextPendingChanges)
  })

  useEventBus(EVENTS.ACCEPT_PENDING_DELETION, ({ testCaseId }) => {
    const acceptedState = getAcceptedPendingDeletionState(
      pendingChanges,
      testCases,
      selectedTestCaseId,
      testCaseId,
    )
    if (!acceptedState) {
      return
    }

    setTestCases(acceptedState.testCases)
    setSelectedTestCaseId(acceptedState.selectedTestCaseId)
    setPendingChanges(acceptedState.pendingChanges)
  })

  useEventBus(EVENTS.DENY_PENDING_DELETION, ({ testCaseId }) => {
    const nextPendingChanges = getRejectedPendingDeletionState(
      pendingChanges,
      testCaseId,
    )
    if (nextPendingChanges === null && pendingChanges?.type !== "DELETE") {
      return
    }

    setPendingChanges(nextPendingChanges)
  })
}
